import { Article } from '../articlesData';

export const articleDigitalFuldmagtMitid: Article = {
  id: 'digital-fuldmagt-skabelon-mitid-guide',
  slug: 'digital-fuldmagt-skabelon-mitid-guide',
  title: 'Digital fuldmagt med MitID: Guide, juridisk gyldighed og skabeloner til virksomheder i 2026',
  subtitle: 'Alt om oprettelse og digital underskrift af fuldmagter med MitID. Lær om generalfuldmagt, bankfuldmagt, procesfuldmagt og partsrepræsentation efter dansk forvaltningsret.',
  metaTitle: 'Digital Fuldmagt med MitID: Guide, Gyldighed & Skabeloner 2026',
  metaDescription: 'Sådan opretter du en juridisk bindende digital fuldmagt med MitID. Læs om generalfuldmagt, bankfuldmagt, skabeloner og digital forsegling med KvikSign.',
  canonicalUrl: 'https://kviksign.com/da/articles/digital-fuldmagt-skabelon-mitid-guide',
  heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FHome%20private.webp?alt=media&token=702d0942-2935-43b0-907d-7dca99624d8a',
  heroImageAlt: 'Oprettelse og digital signering af fuldmagt med MitID',
  category: 'Guides',
  author: {
    name: 'KvikSign',
    role: 'Juridisk Indholdsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-10-28',
  updatedAt: '2026-10-28',
  readingTime: '8 min læsetid',
  keywords: [
    'digital fuldmagt MitID',
    'fuldmagt skabelon digital signatur',
    'generalfuldmagt digital underskrift',
    'bankfuldmagt MitID',
    'fuldmagt til advokat digitalt',
    'juridisk gyldig digital fuldmagt',
    'digital fuldmagt erhverv',
    'partsrepræsentation fuldmagt MitID'
  ],
  keyTakeaways: [
    'En digital fuldmagt underskrevet med MitID har fuld retsgyldighed i henhold til Aftalelovens kapitel 2 om fuldmagter og forvaltningslovens § 8.',
    'MitID-signatur forhindrer tvivl om fuldmagtsgiverens identitet og eliminerer risikoen for forfalskede underskrifter over for banker, myndigheder og modparter.',
    'En fuldmagt bør altid præcisere beføjelsernes omfang, tidsmæssige gyldighed og eventuelle begrænsninger (f.eks. beløbsgrænser eller specifikke sagsnumre).',
    'KvikSign sikrer, at fuldmagten låses med et kryptografisk PAdES-LTV certifikat, så indholdet ikke kan ændres efterfølgende.',
    'Fuldmagtsgiver kan til enhver tid tilbagekalde en fuldmagt ved skriftlig meddelelse eller ved at fastsætte en automatisk udløbsdato i skabelonen.'
  ],
  tableOfContents: [
    { id: 'hvad-er-en-digital-fuldmagt', title: 'Hvad er en digital fuldmagt, og hvordan virker den?' },
    { id: 'forskellige-typer-fuldmagter-i-erhvervslivet', title: 'Forskellige typer fuldmagter i erhvervslivet' },
    { id: 'hvorfor-mitid-er-afgoerende-for-fuldmagtens-sikkerhed', title: 'Hvorfor MitID er afgørende for fuldmagtens sikkerhed' },
    { id: 'hvad-skal-en-god-fuldmagt-indeholde', title: 'Hvad skal en juridisk holdbar fuldmagt indeholde?' },
    { id: 'tilbagekaldelse-og-udloeb-af-fuldmagter', title: 'Tilbagekaldelse og udløb af fuldmagter' },
    { id: 'saadan-opretter-du-en-fuldmagt-i-kviksign', title: 'Sådan opretter og udsender du en fuldmagt i KvikSign' }
  ],
  sections: [
    {
      id: 'hvad-er-en-digital-fuldmagt',
      title: 'Hvad er en digital fuldmagt, og hvordan virker den?',
      paragraphs: [
        'En fuldmagt er en juridisk bemyndigelse, hvor en person eller virksomhed (fuldmagtsgiveren) giver en anden part (fuldmægtigen) ret til at handle og indgå bindende aftaler på sine vegne over for tredjemand.',
        'I en travl hverdag er fuldmagter uundværlige: Advokater handler på vegne af klienter, revisorer repræsenterer virksomheder over for Skattestyrelsen, håndværkere repræsenterer bygherrer over for forsyningsselskaber, og direktører bemyndiger medarbejdere til specifikke indkøb.',
        'Hvor fuldmagter tidligere krævede fysisk papir med risiko for bortkomst eller forfalskning, er den digitale fuldmagt med MitID i dag den sikreste, hurtigste og mest anerkendte metode i Danmark.'
      ]
    },
    {
      id: 'forskellige-typer-fuldmagter-i-erhvervslivet',
      title: 'Forskellige typer fuldmagter i erhvervslivet',
      paragraphs: [
        'Der findes flere former for fuldmagter afhængigt af formålet:',
        '1. Specialfuldmagt (enkeltfuldmagt): Giver ret til at udføre én konkret handling – f.eks. at hente en bil, underskrive en specifik lejekontrakt eller godkende en bestemt byggesag.',
        '2. Generalfuldmagt: Giver vide beføjelser til at varetage alle økonomiske og personlige anliggender for fuldmagtsgiveren.',
        '3. Procesfuldmagt: Gives typisk til advokater til at føre en retssag eller forhandle med myndigheder.',
        '4. Bank- og tegningsfuldmagt: Bemyndigelse til at disponere over en bankkonto eller forpligte en virksomhed inden for en fastsat beløbsramme.'
      ]
    },
    {
      id: 'hvorfor-mitid-er-afgoerende-for-fuldmagtens-sikkerhed',
      title: 'Hvorfor MitID er afgørende for fuldmagtens sikkerhed',
      paragraphs: [
        'Tredjemand – f.eks. en bank, en kommune eller en leverandør – har pligt til at sikre sig, at fuldmagten er ægte. Hvis en fuldmagt blot er sendt som en scannet PDF med en håndskrevet underskrift, vil mange professionelle aktører afvise den, fordi risikoen for svindel er for høj.',
        'Når fuldmagten er underskrevet digitalt med MitID via KvikSign, modtager modtageren et kryptografisk bevis for, at fuldmagtsgiveren har identificeret sig med sit to-faktor MitID. Der kan ikke rejses tvivl om underskriftens ægthed.'
      ]
    },
    {
      id: 'hvad-skal-en-god-fuldmagt-indeholde',
      title: 'Hvad skal en juridisk holdbar fuldmagt indeholde?',
      paragraphs: [
        'For at en fuldmagt er gyldig og kan accepteres af banker og myndigheder, bør den indeholde følgende elementer:'
      ],
      listItems: [
        'Præcise parter: Fulde navn, adresse og CPR-nr. eller CVR-nr. på både fuldmagtsgiver og fuldmægtig.',
        'Klart defineret omfang: Hvad må fuldmægtigen foretage sig? Er der beløbsmæssige lofter?',
        'Tidsmæssig begrænsning: En fast udløbsdato (f.eks. "Gyldig til og med 31. december 2026").',
        'Subfuldmagt: Må fuldmægtigen overdrage beføjelsen til andre (substitution)?',
        'Digital MitID-signatur: Begge parters digitale underskrift og tidsstempel.'
      ]
    },
    {
      id: 'tilbagekaldelse-og-udloeb-af-fuldmagter',
      title: 'Tilbagekaldelse og udløb af fuldmagter',
      paragraphs: [
        'Efter Aftalelovens § 12 kan fuldmagtsgiver som udgangspunkt altid tilbagekalde en fuldmagt.',
        'En digital fuldmagt kan tilbagekaldes ved at give direkte skriftlig meddelelse til fuldmægtigen og de tredjeparter, som fuldmagten er præsenteret for.',
        'I KvikSign kan du oprette skabeloner med forudindstillede udløbsdatoer, så fuldmagten automatisk mister sin gyldighed efter en given periode.'
      ]
    },
    {
      id: 'saadan-opretter-du-en-fuldmagt-i-kviksign',
      title: 'Sådan opretter og udsender du en fuldmagt i KvikSign',
      paragraphs: [
        'Med KvikSign er processen enkel:',
        '1. Vælg din fuldmagtsskabelon eller upload din egen PDF.',
        '2. Indtast fuldmagtsgiverens og fuldmægtigens oplysninger.',
        '3. Vælg MitID eller MitID Erhverv som signeringsmetode.',
        '4. Send fuldmagten. Fuldmagtsgiveren underskriver på 30 sekunder på mobilen.',
        '5. Begge parter modtager automatisk den færdige fuldmagt med revisionslog og certifikat.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Er en digital fuldmagt gyldig over for banker og forsikringsselskaber?',
      answer: 'Ja, når den er underskrevet med MitID, opfylder den kravene til skriftlighed og identifikation i den finansielle sektor.'
    },
    {
      question: 'Kan man lave en digital fremtidsfuldmagt med KvikSign?',
      answer: 'En fremtidsfuldmagt skal oprettes i Tinglysningsrettens Fremtidsfuldmagtsregister og vedkendes for en notar. KvikSign anvendes derimod til alle almindelige generalfuldmagter, erhvervsfuldmagter og specialfuldmagter.'
    },
    {
      question: 'Hvor længe er en digital fuldmagt gyldig?',
      answer: 'Den er gyldig indtil den angivne udløbsdato, eller indtil den aktivt tilbagekaldes af fuldmagtsgiveren.'
    }
  ],
  sources: [
    { title: 'Aftaleloven (Lovbekendtgørelse nr. 193 af 02/03/2016)', url: 'https://www.retsinformation.dk' },
    { title: 'Forvaltningsloven § 8 om partsrepræsentation', url: 'https://www.retsinformation.dk' }
  ],
  disclaimer: 'Artiklen giver et generelt overblik over dansk fuldmagtsret og udgør ikke juridisk rådgivning. Ved komplekse formueforhold anbefales kontakt til en advokat.'
};
